import { finalize, switchMap } from 'rxjs/operators';
import { StoreRequest } from '../../../core/models/mxp-store.model';
import { MxpStoreService } from '../../../core/services/mxp-store.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { GetAccountsByCurrentUserMerchantHttpResponse, MerchantAccountHttpResponse } from '../../../core/models/mxp-account.model';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CountriesEnum } from '../../../core/enums/countries.enum';
import { AuthService } from '@rezolved/auth';
import { User } from '@auth0/auth0-spa-js';
import { BaseDropDownItem, BaseDropDownItemUtils } from '@rezolved/ui';
import { MxpSnackBarService } from '../../../core/services/mxp-snackbar.service';
import { NumberPatternEnum } from 'libs/ui/src/lib/enums/number-pattern.enum';

@Component({
  selector: 'mxp-store-create',
  templateUrl: './store-create.component.html',
  styleUrls: ['./store-create.component.scss'],
})
export class StoreCreateComponent implements OnInit {
  form: FormGroup;
  user!: User;
  countries: BaseDropDownItem[];
  accounts: BaseDropDownItem[] = [];
  isLoading = false;
  phoneFieldHasErrors = false;
  pattern = NumberPatternEnum.POSITIVE_DECIMAL;

  constructor(
    private dialogRef: MatDialogRef<StoreCreateComponent>,
    private mxpStoreService: MxpStoreService,
    private http: HttpClient,
    private authService: AuthService,
    private mxpSnackBarService: MxpSnackBarService,
  ) {
    this.countries = BaseDropDownItemUtils.transformEnum(CountriesEnum);
    this.form = new FormGroup({
      name: new FormControl('', Validators.required),
      phone: new FormControl('', Validators.required),
      street1: new FormControl('', Validators.required),
      city: new FormControl('', Validators.required),
      stateProvince: new FormControl(''),
      postalCode: new FormControl('', Validators.compose([Validators.required, Validators.pattern('\\d+'), Validators.maxLength(5)])),
      country: new FormControl('', Validators.required),
      callbackurl: new FormControl('', Validators.compose([Validators.pattern(
        '^([a-zA-Z]+:\\/\\/)' + // protocol
        '((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IP (v4) address
        '(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-zA-Z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-zA-Z\\d_]*)?$')])),        
      accountId: new FormControl('')
    });

    this.form.get('phone')?.valueChanges.subscribe((x) => {
      this.phoneFieldHasErrors = false;
    });
  }

  ngOnInit(): void {
    this.loadValidatedAccounts();

    this.authService.user$.subscribe((user) => {
      if (user) {
        this.user = user;
      }
    });
  }

  get country(): string {
    return this.form.get(`country`)?.value || ``;
  }

  handlePhoneFieldError() {
    this.phoneFieldHasErrors = true;
  }

  onSave(): void {
    this.isLoading = true;
    const { name, street1, city, stateProvince, postalCode, country, phone, accountId } = this.form.value;
    const callbackWebhookUrl: string = this.form.get('callbackurl')?.value;

    if (this.user) {
      const newStore: StoreRequest = {
        name,
        street1,
        city,
        state: stateProvince,
        zip: postalCode,
        country,
        phone,
        callbackWebhookUrl,
        accountId: accountId || null
      };

      this.mxpSnackBarService
        .wrapApiCall('SNACKBAR.ADD_STORE', this.mxpStoreService.addStore(newStore).pipe(
          switchMap(response => this.mxpStoreService.getStore(response.id)),
        ))
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe(
          response => this.dialogRef.close(response.store),
          error => console.log(error),
        );
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private loadValidatedAccounts(): void {
    this.isLoading = true;

    this.mxpSnackBarService
      .wrapApiCall(
        'SNACKBAR.GET_ACCOUNTS',
        this.http.get<GetAccountsByCurrentUserMerchantHttpResponse>(`${environment.identityUrl}/api/mxp-accounts/current-user-merchant`),
        undefined,
        undefined,
        undefined,
        true,
      )
      .subscribe(
        (response: GetAccountsByCurrentUserMerchantHttpResponse) => {
          this.accounts = [
            { key: 'NO_ACCOUNT', value: '' },
            ...(response.accounts || [])
              .filter((account) => account.validationStatus === 'ValidationSucceded')
              .map((account) => this.toAccountDropDownItem(account)),
          ];
          this.isLoading = false;
        },
        (error) => {
          console.log(error);
          this.isLoading = false;
        },
      );
  }

  private toAccountDropDownItem(account: MerchantAccountHttpResponse): BaseDropDownItem {
    return {
      key: account.accountNumber || '',
      value: account.accountId || '',
    };
  }

}
